import { CurrencyPipe, DatePipe } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ErrorHandler, importProvidersFrom, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { ErrorStateMatcher, MatNativeDateModule } from '@angular/material/core';
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogModule } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, Router, RouteReuseStrategy, ROUTES } from '@angular/router';
import { IonicRouteStrategy, provideIonicAngular } from '@ionic/angular/standalone';
import {
  popperVariation,
  provideTippyConfig,
  provideTippyLoader,
  tooltipVariation,
  withContextMenuVariation,
} from '@ngneat/helipopper/config';
import { PhoneMaskPipe } from '@pipes/phoneMask.pipe';
import * as Sentry from '@sentry/angular';
import { ApplicationResolutionService } from '@services/application-resolution.service';
import { InitAppService } from '@services/init-app.service';
import { InstantErrorStateMatcher } from '@utils/error-matchers';
import { NgOtpInputModule } from 'ng-otp-input';
import { NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { NgxStripeModule } from 'ngx-stripe';
import 'zone.js';
import { ErrorHandlerInterceptor } from './app/interceptors/error-handler.interceptor';
import { AuthHttpInterceptor } from './app/interceptors/token.interceptor';
import { desktopRoutes, mobileRoutes } from './routes';

export function initUserProviderFactory(provider: InitAppService) {
  return async () => provider.initApp();
}

export function getDynamicRoutes(appResolutionService: ApplicationResolutionService) {
  return appResolutionService.isMobileResolution ? mobileRoutes : desktopRoutes;
}
export default [
  importProvidersFrom(
    BrowserModule,
    MatDialogModule,
    MatSnackBarModule,
    NgOtpInputModule,
    MatNativeDateModule,
    NgxStripeModule.forRoot(import.meta.env.NG_APP_STRIPE_CLIENT_KEY)
  ),
  {
    provide: ErrorHandler,
    useValue: Sentry.createErrorHandler({
      showDialog: false,
    }),
  },
  {
    provide: Sentry.TraceService,
    deps: [Router],
  },
  { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
  ApplicationResolutionService,
  provideIonicAngular(),
  provideZoneChangeDetection(),
  NgxMaskPipe,
  DatePipe,
  PhoneMaskPipe,
  CurrencyPipe,
  provideNgxMask(),
  { provide: ErrorStateMatcher, useClass: InstantErrorStateMatcher },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: AuthHttpInterceptor,
    multi: true,
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: ErrorHandlerInterceptor,
    multi: true,
  },
  {
    provide: MAT_DIALOG_DEFAULT_OPTIONS,
    useValue: { autoFocus: false },
  },
  provideAppInitializer(() => inject(InitAppService).initApp()),
  { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline' } },
  provideAnimations(),
  provideHttpClient(withInterceptorsFromDi()),
  {
    provide: ROUTES,
    useFactory: getDynamicRoutes,
    deps: [ApplicationResolutionService],
    multi: true,
  },
  provideRouter([]),
  provideTippyLoader(() => import('tippy.js')),
  provideTippyConfig({
    defaultVariation: 'tooltip',
    trigger: 'mouseenter',
    variations: {
      tooltip: tooltipVariation,
      popper: popperVariation,
      contextMenu: withContextMenuVariation(popperVariation),
    },
  }),
];
